import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { CalendarioAnualApiDto, HitoCalendarioApiDto } from '../contracts/backend-api.contracts';
import { HITOS_CALENDARIO_MOCK } from '../mocks/calendario.mock';
import { CalendarioAnual, CalendarioMes, HitoCalendario } from '../models/calendario.models';
import { environment } from '../../../environments/environment';

export const MESES_ES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

@Injectable({ providedIn: 'root' })
export class CalendarioService {
  private readonly baseUrl = `${environment.apiBaseUrl}/calendario`;

  constructor(private readonly http: HttpClient) {}

  getNombresMeses(): string[] {
    return [...MESES_ES];
  }

  getCalendarioPorAnio(anio: number): Observable<CalendarioAnual> {
    if (!environment.useMockApi) {
      return this.http
        .get<CalendarioAnualApiDto>(`${this.baseUrl}/anual`, { params: { anio } })
        .pipe(map((dto) => this.fromApiAnual(dto)));
    }

    const meses: CalendarioMes[] = Array.from({ length: 12 }, (_, i) => this.buildMes(i, anio));
    const payload: CalendarioAnualApiDto = {
      anio,
      meses: meses.map((mes) => this.toApiMes(mes)),
    };
    return of(this.fromApiAnual(payload));
  }

  getHitosPorMes(anio: number, mesIndex: number): Observable<HitoCalendario[]> {
    if (!environment.useMockApi) {
      return this.http
        .get<HitoCalendarioApiDto[]>(`${this.baseUrl}/hitos`, { params: { anio, mes: mesIndex + 1 } })
        .pipe(map((items) => items.map((dto) => this.fromApiHito(dto))));
    }

    const payload = HITOS_CALENDARIO_MOCK.map((hito) => this.toApiHito(hito));
    const hitos = payload.map((dto) => this.fromApiHito(dto)).filter((hito) => {
      const fecha = new Date(`${hito.fecha}T00:00:00`);
      return fecha.getFullYear() === anio && fecha.getMonth() === mesIndex;
    });

    return of(hitos);
  }

  private buildMes(indexMes: number, anio: number): CalendarioMes {
    return {
      numeroMes: indexMes + 1,
      nombreMes: MESES_ES[indexMes],
    };
  }

  private fromApiAnual(dto: CalendarioAnualApiDto): CalendarioAnual {
    return {
      anio: dto.anio,
      meses: dto.meses.map((mes) => ({
        numeroMes: mes.numero_mes,
        nombreMes: mes.nombre_mes,
      })),
    };
  }

  private toApiMes(model: CalendarioMes): CalendarioAnualApiDto['meses'][number] {
    return {
      numero_mes: model.numeroMes,
      nombre_mes: model.nombreMes,
    };
  }

  private fromApiHito(dto: HitoCalendarioApiDto): HitoCalendario {
    return {
      id: dto.id,
      nombre: dto.nombre,
      fecha: dto.fecha,
      estado: dto.estado ?? 'abierto',
      subdireccion: dto.subdireccion ?? 'Sin subdireccion',
    };
  }

  private toApiHito(model: HitoCalendario): HitoCalendarioApiDto {
    return {
      id: model.id,
      nombre: model.nombre,
      fecha: model.fecha,
      estado: model.estado,
      subdireccion: model.subdireccion,
    };
  }
}
